import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Database, Loader2, ServerCrash, Power, RefreshCw, Folder, Search, 
  Table2, FileJson, ChevronRight, ChevronLeft, Copy, Check, Filter, 
  Layers, Code2, Sparkles, SlidersHorizontal
} from 'lucide-react';
import io from 'socket.io-client';

import SharedSSHUnlock from './SharedSSHUnlock';

const MongoTab = ({ tenant, isVisible, sshSessionId, socket, sshStatus, sshError, onConnect, onDisconnect, isLeftPanelOpen, onToggleLeftPanel }) => {
  // Mongo UI State
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [viewMode, setViewMode] = useState('json'); // 'json' or 'table'
  const [errorMessage, setErrorMessage] = useState('');
  
  // UI Enhancements
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [collectionSearch, setCollectionSearch] = useState('');
  const [documentSearch, setDocumentSearch] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  // Pagination State
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);

  const prevIsVisible = useRef(isVisible);

  useEffect(() => {
    if (isVisible && socket) {
      const onMongoResult = (res) => {
        setLoadingData(false);
        if (res.success) {
          if (res.queryType === 'getCollections') {
            setCollections(res.data || []);
            if (res.data && res.data.length > 0 && !selectedCollection) {
              setSelectedCollection(res.data[0]);
              setPage(1);
              fetchDocuments(res.data[0], 1);
            }
          } else if (res.queryType === 'getDocuments') {
            setDocuments(res.data || []);
            if (res.pagination) {
              setPage(res.pagination.page);
              setTotalPages(res.pagination.totalPages);
              setTotalDocs(res.pagination.totalDocs);
            }
          }
        } else {
          setErrorMessage(`Mongo Error: ${res.error}`);
        }
      };

      socket.on('ssh:mongoResult', onMongoResult);
      
      // Auto-fetch if we are connected, have no collections yet, and haven't encountered an error
      if (sshStatus === 'connected' && collections.length === 0 && !loadingData && !errorMessage) {
          fetchCollections();
      }

      return () => {
          socket.off('ssh:mongoResult', onMongoResult);
      }
    }
  }, [isVisible, sshStatus, socket, collections.length, loadingData, selectedCollection, errorMessage]);

  useEffect(() => {
    if (isVisible && !prevIsVisible.current) {
        setErrorMessage('');
    }
    prevIsVisible.current = isVisible;
  }, [isVisible]);

  // When connection drops, reset state
  useEffect(() => {
      if (sshStatus !== 'connected') {
          setCollections([]);
          setDocuments([]);
          setSelectedCollection(null);
      }
  }, [sshStatus]);

  const fetchCollections = (currentSessionId = sshSessionId) => {
    if (!socket || !currentSessionId) return;
    setLoadingData(true);
    setErrorMessage('');
    socket.emit('ssh:mongoQuery', {
      sessionId: currentSessionId,
      dbName: tenant?.DbName,
      mongoPassword: tenant?.mongoPassword,
      queryType: 'getCollections'
    });
  };

  const fetchDocuments = (colName, pageToFetch = 1) => {
    if (!socket || !sshSessionId) return;
    if (colName !== selectedCollection) {
        setPage(1);
        setDocumentSearch('');
    }
    setSelectedCollection(colName);
    setLoadingData(true);
    setErrorMessage('');
    socket.emit('ssh:mongoQuery', {
      sessionId: sshSessionId,
      dbName: tenant?.DbName,
      mongoPassword: tenant?.mongoPassword,
      queryType: 'getDocuments',
      collectionName: colName,
      page: pageToFetch,
      limit: 30
    });
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(typeof text === 'object' ? JSON.stringify(text, null, 2) : text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Filter collections by search term
  const filteredCollections = useMemo(() => {
    if (!collectionSearch.trim()) return collections;
    return collections.filter(c => c.toLowerCase().includes(collectionSearch.toLowerCase().trim()));
  }, [collections, collectionSearch]);

  // Filter documents locally by search term
  const filteredDocuments = useMemo(() => {
    if (!documentSearch.trim()) return documents;
    const term = documentSearch.toLowerCase().trim();
    return documents.filter(doc => JSON.stringify(doc).toLowerCase().includes(term));
  }, [documents, documentSearch]);

  // Helper to format JSON values in table
  const formatValue = (val) => {
    if (val === null) return 'null';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  // Get unique keys from documents for table headers
  const getTableHeaders = () => {
    if (!documents || documents.length === 0) return [];
    const keys = new Set();
    documents.forEach(doc => {
      Object.keys(doc).forEach(key => keys.add(key));
    });
    const sorted = Array.from(keys);
    if (sorted.includes('_id')) {
      return ['_id', ...sorted.filter(k => k !== '_id')];
    }
    return sorted;
  };

  // Syntax highlighting for JSON
  const renderColorizedJson = (obj) => {
    const jsonStr = JSON.stringify(obj, null, 2);
    const coloredHtml = jsonStr.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
      let cls = 'text-sky-400'; // number
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'text-violet-300 font-medium'; // key
        } else {
          cls = 'text-emerald-400'; // string
        }
      } else if (/true|false/.test(match)) {
        cls = 'text-amber-400 font-bold'; // boolean
      } else if (/null/.test(match)) {
        cls = 'text-slate-500 italic'; // null
      }
      return `<span class="${cls}">${match}</span>`;
    });

    return (
      <pre 
        className="text-sm font-mono leading-relaxed overflow-x-auto text-slate-200" 
        dangerouslySetInnerHTML={{ __html: coloredHtml }} 
      />
    );
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-slate-100 overflow-hidden">
      {/* Top Header Bar */}
      <div className="h-16 px-4 lg:px-6 border-b border-slate-200 flex items-center justify-between bg-white shrink-0 z-10 shadow-xs">
        <div className="flex items-center gap-3">
          {!isLeftPanelOpen && (
            <button
              onClick={onToggleLeftPanel}
              className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg border border-slate-200 transition-colors shrink-0"
              title="Déplier Fiche Instance"
            >
              <ChevronRight size={18} />
            </button>
          )}
          <div className="p-2.5 bg-violet-50 text-violet-600 rounded-lg border border-violet-100">
            <Database size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-slate-800 tracking-tight">Console MongoDB</h3>
              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-mono">
                {tenant?.DbName || 'Database'}
              </span>
            </div>
            <div className="text-xs font-medium flex items-center gap-1.5 mt-0.5">
              {sshStatus === 'connected' && (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]"></span>
                  <span className="text-emerald-600 font-semibold">Connecté au cluster</span>
                </>
              )}
              {sshStatus === 'connecting' && (
                <>
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                  <span className="text-amber-600 font-semibold">Connexion en cours...</span>
                </>
              )}
              {sshStatus === 'idle' && (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-300"></span>
                  <span className="text-slate-500">Prêt</span>
                </>
              )}
              {sshStatus === 'error' && (
                <>
                  <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                  <span className="text-rose-600 font-semibold">Déconnecté</span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {sshStatus === 'connected' && (
            <>
              <button
                onClick={() => fetchCollections()}
                disabled={loadingData}
                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-slate-700 hover:text-violet-700 bg-slate-50 hover:bg-violet-50 border border-slate-200 rounded-lg transition-all"
                title="Actualiser les collections"
              >
                <RefreshCw size={15} className={loadingData && !selectedCollection ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">Actualiser</span>
              </button>
              <button
                onClick={() => {
                  if (onDisconnect) {
                    onDisconnect();
                  } else {
                    if (tenant?.id) sessionStorage.removeItem(`ssh_pass_${tenant.id}`);
                    if (socket && sshSessionId) {
                      socket.emit('ssh:disconnect', { sessionId: sshSessionId });
                    }
                  }
                }}
                className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-rose-600 bg-rose-50 border border-rose-200 hover:bg-rose-600 hover:text-white rounded-lg transition-all duration-200"
              >
                <Power size={15} />
                <span>Déconnecter</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {errorMessage && sshStatus === 'connected' && (
        <div className="bg-rose-50 border-b border-rose-200 text-rose-700 px-4 py-2.5 text-sm flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <ServerCrash size={16} className="shrink-0" />
            <span className="font-semibold">{errorMessage}</span>
          </div>
          <button onClick={() => setErrorMessage('')} className="text-xs text-rose-500 hover:underline font-bold">Fermer</button>
        </div>
      )}

      {/* Main Container */}
      <div className="flex-1 flex overflow-hidden relative">
        {sshStatus !== 'connected' ? (
          <SharedSSHUnlock 
            tenant={tenant} 
            status={sshStatus} 
            errorMessage={sshError} 
            onConnect={onConnect} 
            title="Accès Sécurisé MongoDB"
            description="Connectez-vous via SSH au VPS pour accéder directement aux collections MongoDB sans exposer les ports."
          />
        ) : errorMessage && (errorMessage.includes('arrêté') || errorMessage.includes('Impossible de trouver') || errorMessage.includes('Mongo Error')) ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 bg-slate-50 text-center">
            <div className="p-4 bg-rose-50 border border-rose-200 text-rose-600 rounded-2xl mb-4 shadow-sm">
              <Database size={44} className="animate-pulse" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Base de Données MongoDB Arrêtée</h3>
            <p className="text-xs text-slate-500 max-w-md mb-6 leading-relaxed">
              Le conteneur <code className="font-mono bg-slate-200 px-1.5 py-0.5 rounded text-slate-800 text-xs">mongo_{tenant?.DbName}</code> est actuellement éteint sur le serveur VPS.
              <br />Veuillez démarrer l'instance depuis la barre d'actions ci-dessus.
            </p>
            <button
              onClick={() => fetchCollections()}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-2"
            >
              <RefreshCw size={14} /> Réessayer la Connexion
            </button>
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden w-full">
            {/* Sidebar: Collections */}
            <div 
              className={`border-r border-slate-200 bg-slate-50 flex flex-col transition-all duration-300 shrink-0 ${
                isSidebarOpen ? 'w-60 sm:w-68 lg:w-76' : 'w-0 border-r-0 overflow-hidden'
              }`}
            >
              {/* Sidebar Header & Search */}
              <div className="p-3.5 border-b border-slate-200 bg-white space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-extrabold text-slate-700 uppercase tracking-wider">
                    <Layers size={15} className="text-violet-500" />
                    <span>Collections</span>
                    <span className="ml-auto text-xs font-extrabold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">
                      {filteredCollections.length}
                    </span>
                  </div>
                </div>

                {/* Collection Filter Input */}
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Filtrer collection..."
                    value={collectionSearch}
                    onChange={(e) => setCollectionSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-xs font-medium bg-slate-50 border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:border-violet-400 focus:bg-white transition-all"
                  />
                </div>
              </div>

              {/* Collection List */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {loadingData && collections.length === 0 ? (
                  <div className="p-2 space-y-2.5 animate-pulse">
                    <div className="flex items-center gap-2 p-2.5 bg-violet-50 border border-violet-100 rounded-lg text-xs font-bold text-violet-700">
                      <Loader2 size={14} className="animate-spin text-violet-600 shrink-0" />
                      <span>Connexion MongoDB...</span>
                    </div>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="h-9 bg-slate-200/70 rounded-lg w-full" />
                    ))}
                  </div>
                ) : filteredCollections.length === 0 && !loadingData ? (
                  <div className="text-center py-8 text-xs text-slate-400 italic">
                    {collectionSearch ? 'Aucune collection trouvée.' : 'Aucune collection.'}
                  </div>
                ) : (
                  filteredCollections.map(col => {
                    const isSelected = selectedCollection === col;
                    return (
                      <button
                        key={col}
                        onClick={() => fetchDocuments(col)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-xs font-semibold transition-all ${
                          isSelected 
                            ? 'bg-violet-600 text-white font-bold shadow-xs' 
                            : 'text-slate-700 hover:bg-slate-200/70 hover:text-slate-900'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 truncate">
                          <Folder size={15} className={isSelected ? 'text-violet-200' : 'text-slate-400'} />
                          <span className="truncate">{col}</span>
                        </div>
                        {isSelected && <ChevronRight size={14} className="text-violet-200 shrink-0" />}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Main Area: Document Inspector */}
            <div className="flex-1 flex flex-col min-w-0 bg-white">
              {/* Document Toolbar */}
              <div className="h-14 border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 bg-slate-50/80 shrink-0 gap-3 overflow-x-auto">
                <div className="flex items-center gap-3 min-w-0">
                  {/* Toggle Sidebar Button */}
                  <button
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    className="p-2 text-slate-600 hover:text-slate-900 hover:bg-white rounded-lg border border-slate-200 shadow-xs transition-colors shrink-0"
                    title={isSidebarOpen ? "Masquer volet des collections" : "Afficher collections"}
                  >
                    {isSidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                  </button>

                  <div className="flex items-center gap-2 font-mono text-sm font-bold text-slate-800 truncate">
                    {selectedCollection ? (
                      <>
                        <span className="text-slate-400 font-normal truncate hidden sm:inline">{tenant?.DbName}</span>
                        <span className="text-slate-300 hidden sm:inline">/</span>
                        <span className="text-violet-600 font-black truncate text-sm">{selectedCollection}</span>
                        {totalDocs > 0 && (
                          <span className="ml-2 text-xs font-sans font-bold px-2.5 py-0.5 rounded-full bg-slate-200 text-slate-700 shrink-0">
                            {totalDocs} docs
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-slate-400 italic font-sans font-normal text-xs">Sélectionnez une collection</span>
                    )}
                  </div>

                  {loadingData && selectedCollection && (
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-violet-600 bg-violet-50 border border-violet-100 px-2.5 py-1 rounded-md shrink-0">
                      <Loader2 size={13} className="animate-spin" /> Chargement...
                    </span>
                  )}
                </div>

                {selectedCollection && (
                  <div className="flex items-center gap-2.5 shrink-0">
                    {/* Document Search Filter */}
                    <div className="relative hidden md:block">
                      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Recherche rapide doc..."
                        value={documentSearch}
                        onChange={(e) => setDocumentSearch(e.target.value)}
                        className="pl-8 pr-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-md text-slate-800 placeholder-slate-400 focus:outline-none focus:border-violet-400 w-44 lg:w-56 transition-all"
                      />
                    </div>

                    {/* View Switcher (JSON / Table) */}
                    <div className="flex bg-slate-200/80 p-0.5 rounded-lg border border-slate-200">
                      <button 
                        onClick={() => setViewMode('json')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                          viewMode === 'json' ? 'bg-white text-violet-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        <FileJson size={14} /> JSON
                      </button>
                      <button 
                        onClick={() => setViewMode('table')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                          viewMode === 'table' ? 'bg-white text-violet-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        <Table2 size={14} /> Table
                      </button>
                    </div>

                    <button 
                      onClick={() => fetchDocuments(selectedCollection, page)}
                      className="p-2 text-slate-600 hover:text-violet-600 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition-colors shadow-xs"
                      title="Rafraîchir les documents"
                    >
                      <RefreshCw size={14} />
                    </button>
                  </div>
                )}
              </div>

              {/* Document Data Viewport */}
              <div className="flex-1 overflow-auto p-4 lg:p-6 bg-slate-100/60">
                {loadingData ? (
                  <div className="space-y-4 animate-pulse">
                    <div className="p-4 bg-violet-50 border border-violet-100 rounded-xl flex items-center gap-3 text-violet-800 text-xs font-bold shadow-xs">
                      <Loader2 size={18} className="animate-spin text-violet-600 shrink-0" />
                      <span>Récupération des documents <span className="font-mono text-violet-900 font-black">{selectedCollection || tenant?.DbName}</span> en cours...</span>
                    </div>

                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-3 shadow-md">
                        <div className="flex justify-between items-center border-b border-slate-800 pb-3 mb-2">
                          <div className="w-48 h-4 bg-slate-800 rounded" />
                          <div className="w-20 h-6 bg-slate-800 rounded" />
                        </div>
                        <div className="w-3/4 h-4 bg-slate-800/60 rounded" />
                        <div className="w-1/2 h-4 bg-slate-800/60 rounded" />
                        <div className="w-2/3 h-4 bg-slate-800/60 rounded" />
                      </div>
                    ))}
                  </div>
                ) : !selectedCollection ? (
                  <div className="h-full flex flex-col items-center justify-center text-slate-400 p-6">
                    <div className="w-16 h-16 rounded-2xl bg-slate-200/60 flex items-center justify-center mb-3 text-slate-400">
                      <Database size={32} />
                    </div>
                    <p className="font-semibold text-slate-600 text-sm">Aucune collection sélectionnée</p>
                    <p className="text-xs text-slate-400 mt-1">Choisissez une collection dans le panneau de gauche pour inspecter ses données.</p>
                  </div>
                ) : filteredDocuments.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-slate-400 bg-white rounded-xl border border-dashed border-slate-200 p-6">
                    <Search size={36} className="mb-2 opacity-30 text-violet-500" />
                    <p className="font-bold text-slate-700 text-sm">Aucun document trouvé</p>
                    <p className="text-xs text-slate-400 mt-1">Cette collection est vide ou aucun document ne correspond à la recherche.</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-w-full">
                    {viewMode === 'json' ? (
                      /* Enhanced Dark Slate Code Block JSON View */
                      filteredDocuments.map((doc, idx) => {
                        const docIdStr = doc._id 
                          ? (typeof doc._id === 'object' ? doc._id.$oid || JSON.stringify(doc._id) : String(doc._id))
                          : `doc_${idx}`;

                        return (
                          <div key={docIdStr || idx} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-md">
                            {/* Card Top Header */}
                            <div className="bg-slate-950/80 border-b border-slate-800/80 px-4 py-2.5 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Code2 size={14} className="text-violet-400" />
                                <span className="text-xs font-mono font-bold text-slate-200">
                                  {doc._id ? `_id: ${docIdStr}` : `Document ${idx + 1}`}
                                </span>
                              </div>
                              <button
                                onClick={() => copyToClipboard(doc, docIdStr)}
                                className="flex items-center gap-1.5 text-xs font-bold text-slate-300 hover:text-white bg-slate-800/80 hover:bg-slate-800 px-2.5 py-1 rounded-md transition-colors"
                              >
                                {copiedId === docIdStr ? (
                                  <>
                                    <Check size={13} className="text-emerald-400" />
                                    <span className="text-emerald-400">Copié</span>
                                  </>
                                ) : (
                                  <>
                                    <Copy size={13} />
                                    <span>Copier JSON</span>
                                  </>
                                )}
                              </button>
                            </div>
                            
                            {/* Code Container */}
                            <div className="p-4 lg:p-5 overflow-x-auto bg-[#0d1117]">
                              {renderColorizedJson(doc)}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      /* Responsive Table View */
                      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs w-full overflow-x-auto">
                        <table className="w-full text-left border-collapse whitespace-nowrap">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                              {getTableHeaders().map(header => (
                                <th key={header} className="px-4 py-3 font-mono text-xs font-black text-slate-700 uppercase tracking-wider">
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 text-xs">
                            {filteredDocuments.map((doc, idx) => (
                              <tr key={doc._id || idx} className="hover:bg-violet-50/40 transition-colors">
                                {getTableHeaders().map(header => (
                                  <td key={`${idx}-${header}`} className="px-4 py-3 font-mono text-slate-800 max-w-xs truncate font-medium" title={formatValue(doc[header])}>
                                    {formatValue(doc[header])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    
                    {/* Pagination Bar */}
                    {totalPages > 1 && (
                      <div className="flex flex-col sm:flex-row items-center justify-between bg-white px-5 py-3.5 border border-slate-200 rounded-xl shadow-xs gap-3 mt-4">
                        <div className="text-xs text-slate-600 font-medium">
                          Affichage de <span className="font-bold text-slate-900">{(page - 1) * 30 + 1}</span> à <span className="font-bold text-slate-900">{Math.min(page * 30, totalDocs)}</span> sur <span className="font-bold text-slate-900">{totalDocs}</span> documents
                        </div>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => fetchDocuments(selectedCollection, page - 1)}
                            disabled={page === 1 || loadingData}
                            className="px-3.5 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-xs"
                          >
                            Précédent
                          </button>
                          <div className="text-xs font-bold text-slate-800 px-2 font-mono">
                            {page} / {totalPages}
                          </div>
                          <button 
                            onClick={() => fetchDocuments(selectedCollection, page + 1)}
                            disabled={page === totalPages || loadingData}
                            className="px-3.5 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-xs"
                          >
                            Suivant
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(MongoTab);

